import {
    JobAssistApi,
    GetBattlecardV3Params,
    LoanApplicationSubmitSource2,
} from '@servicetitan/financing-api-client';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
    ApplicationDataStore,
    getApplicationDataDefaultValues,
} from '@servicetitan/financing-application-shared';

export const useCustomerData = ({
    jobId,
    tenantId,
    technicianId,
}: {
    jobId: string;
    tenantId: string;
    technicianId: string;
}) => {
    const { setApplicationData } = ApplicationDataStore.useStore();
    const [jobAssistApiClient] = useDependencies(JobAssistApi);
    const queryEnabled = !!jobId && !!tenantId && !!technicianId;
    const [hasAppliedCustomerData, setHasAppliedCustomerData] = useState(!queryEnabled);
    // These defaults replace the whole application data, so they may only be seeded once. The
    // battlecard query refetches on window focus, which the FMA webview triggers routinely, and a
    // second pass would wipe everything the applicant already entered (SSN, date of birth, income,
    // co-applicant) — including the soft-pull answers the hard credit application pre-populates.
    const hasSeededDefaultsRef = useRef(false);

    // Query for battlecard data to get comprehensive job, customer, and location info
    const battlecardResult = useQuery({
        queryKey: ['getBattlecardV3', jobId, tenantId, technicianId],
        queryFn: () => {
            const params: GetBattlecardV3Params = {
                jobId: Number(jobId),
                limit: 1,
                threadpoolLimit: 3,
                pagesize: 5,
            };

            return jobAssistApiClient.getBattlecardV3(tenantId, technicianId, params);
        },
        enabled: queryEnabled,
        throwOnError: false,
    });

    const battlecardData = battlecardResult.data?.data;

    useEffect(() => {
        if (!queryEnabled || battlecardResult.isError) {
            setHasAppliedCustomerData(true);
            return;
        }

        if (!battlecardResult.isSuccess || hasSeededDefaultsRef.current) {
            return;
        }

        hasSeededDefaultsRef.current = true;

        if (battlecardData) {
            // Extract customer name from battlecard response
            const fullName = battlecardData.CustomerInfo?.Details?.CustomerName ?? '';
            const nameParts = fullName.trim().split(' ');
            const firstName = nameParts[0] ?? '';
            const lastName = nameParts.slice(1).join(' ') ?? '';

            // Get customer email directly from battlecard response
            const customerEmail = battlecardData.CustomerInfo?.Details?.CustomerEmail ?? '';
            const customerPhone = battlecardData.CustomerInfo?.Details?.CustomerPhoneNumber ?? '';

            // Get location data from LocationInfo.LocationForJob
            const jobLocation = battlecardData.LocationInfo?.LocationForJob;

            // Try to get structured address from customer locations matching the job location
            const structuredLocation = battlecardData.CustomerInfo?.Locations?.find(
                location => location.Id === jobLocation?.Id,
            );
            const locationAddress = structuredLocation?.Address;

            setApplicationData(
                getApplicationDataDefaultValues({
                    customerFirstName: firstName,
                    customerLastName: lastName,
                    customerEmail,
                    customerPhone,
                    serviceLocationStreet: locationAddress?.Street ?? '',
                    serviceLocationUnit: locationAddress?.Unit ?? '',
                    serviceLocationCity: locationAddress?.City ?? '',
                    serviceLocationState: locationAddress?.State ?? '',
                    serviceLocationZip: locationAddress?.Zip ?? '',
                    source: LoanApplicationSubmitSource2.Mobile,
                }),
            );
        }

        // Mark applied after a successful response (including a null payload) so the
        // applicant form does not mount before default values are written to the store.
        setHasAppliedCustomerData(true);
    }, [
        queryEnabled,
        battlecardResult.isError,
        battlecardResult.isSuccess,
        battlecardData,
        setApplicationData,
    ]);

    return {
        battlecardResult,
        battlecardData,
        isLoading: queryEnabled && !hasAppliedCustomerData,
        error: battlecardResult.error,
    };
};
